/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  Ruler,
  Thermometer,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const hvacConfig: SolutionTemplateConfig = {
  tradeName: "HVAC",
  tradeSlug: "hvac",
  hero: {
    badge: "HVAC Business Platform",
    title: "Software for HVAC contractors",
    titleHighlight: "from install to service call.",
    description:
      "What did that RTU replacement actually cost? Are your maintenance agreements profitable? Is your labor burden eating your margin? One platform for equipment costs, estimates, projects, service contracts, and invoices.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded HVAC costs",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for HVAC contractors",
    proofPoints: [
      { icon: Thermometer, text: "Install, replacement, and maintenance" },
      { icon: DollarSign, text: "Equipment costs front and center" },
      { icon: Wrench, text: "Maintenance agreements with visit tracking" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running an HVAC business without a system.",
    items: [
      {
        icon: Clock,
        title: "Equipment specs drive the entire bid",
        description:
          "A single RTU can be $8-15K. Spec the wrong unit, miss a rebate, or forget startup labor — and your margin disappears on the biggest line item.",
      },
      {
        icon: DollarSign,
        title: "Maintenance agreements tracked on a spreadsheet",
        description:
          "You're running 40 maintenance contracts but tracking visits, costs, and renewals across spreadsheets, emails, and memory. One missed visit and you lose the contract.",
      },
      {
        icon: FileText,
        title: "Duct quantities from memory",
        description:
          "Linear feet of duct, pounds of refrigerant, number of diffusers — you know the numbers intuitively but they're not written down anywhere you can track against.",
      },
    ],
  },
  stats: [
    { value: "Install + Service", label: "Both in one system" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From equipment specs to maintenance renewal.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Configure your equipment catalog with RTU specs, split system costs, and mini-split pricing. Set journeyman and helper labor rates. Build work packages — an RTU replacement bundle (unit + curb adapter + ductwork + controls + startup) you reuse on every job.",
        icon: Thermometer,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload mechanical drawings, measure duct runs and equipment pads, and pull in your work packages. Equipment, ductwork, piping, and labor — all priced together. Send a branded proposal with equipment specs and warranty terms.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track tech hours, log material costs against the estimate, manage warranty callbacks. Budget vs. actuals — know if the equipment markup held before the job closes.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. Track which equipment lines are most profitable. Ask Hank: 'What's my average margin on RTU replacements this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that understands equipment-heavy bids, maintenance agreements, and HVAC-specific labor.",
    items: [
      {
        icon: Layers,
        title: "Work packages for common installs",
        description:
          "RTU replacement (unit + curb + duct transition + controls + startup), split system install (condenser + air handler + lineset + electrical), duct replacement (duct + insulation + hangers + sealing).",
        bullets: [
          "Material + labor + equipment bundles",
          "Production rates for HVAC tasks",
          "Configurable per job",
          "Track usage across estimates",
        ],
      },
      {
        icon: Truck,
        title: "Equipment and supplier pricing",
        description:
          "Track equipment costs by manufacturer and model. Import distributor pricing. When your supply house updates RTU pricing, update once — every estimate uses the new number.",
        bullets: [
          "Equipment TCO tracking",
          "Per-supplier pricing",
          "Import from distributor spreadsheets",
          "Subcontractor management for controls",
        ],
      },
      {
        icon: Wrench,
        title: "Maintenance agreements",
        description:
          "Preventive maintenance agreements are steady revenue. CC tracks scheduled visits, logs filter changes and refrigerant charges per visit, and keeps service work connected to the client record.",
        bullets: [
          "Recurring service management",
          "Visit logging with duration and notes",
          "Cost tracking per agreement",
          "Service schedule with calendar view",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs",
        description:
          "Upload mechanical drawings and measure duct runs, equipment pad dimensions, and piping lengths directly on screen. Quantities feed into your estimate through work packages.",
        bullets: [
          "Measure on mechanical plans",
          "Scale calibration",
          "Measurements link to line items",
          "Multi-page plan support",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Equipment specs scattered across emails and PDFs" },
      { icon: X, text: "Maintenance visits tracked on a calendar app" },
      { icon: X, text: "Duct quantities estimated from experience" },
      { icon: X, text: "No idea if your equipment markup held" },
      { icon: X, text: "Generic proposals without equipment specs" },
      { icon: X, text: "Tech hours tracked on paper" },
    ],
    after: [
      {
        icon: CheckCircle,
        text: "Equipment catalog with current distributor pricing",
      },
      {
        icon: CheckCircle,
        text: "Maintenance agreements with visit and cost tracking",
      },
      {
        icon: CheckCircle,
        text: "Blueprint takeoffs for accurate duct quantities",
      },
      { icon: CheckCircle, text: "Budget vs. actuals on every job" },
      {
        icon: CheckCircle,
        text: "Branded proposals with equipment specs and warranty",
      },
      { icon: CheckCircle, text: "Time entries with approval workflow" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your equipment costs feed into estimates, your actuals feed back, and your maintenance agreements are tracked alongside installs.",
    metrics: [
      {
        value: "Accurate",
        label: "Equipment costs from current distributor pricing",
      },
      { value: "Faster", label: "Proposals with specs sent the same day" },
      { value: "Visible", label: "Margin per job — install and service" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions HVAC contractors ask",
    items: [
      {
        question: "Does this handle both installs and maintenance?",
        answer:
          "Yes. Install work is tracked as projects with budget vs. actuals. Maintenance agreements are tracked as services with visit logging and cost tracking. Both share the same cost database and client records.",
      },
      {
        question: "Can I track equipment costs by manufacturer?",
        answer:
          "Yes. Build your equipment catalog with specs, costs, and supplier info. Import pricing from your distributor. Track total cost of ownership.",
      },
      {
        question: "How do work packages work for HVAC?",
        answer:
          "Build reusable bundles — an 'RTU Replacement' package includes the unit, curb adapter, ductwork transition, controls, and startup labor. Pull it into any estimate and adjust for the specific job.",
      },
      {
        question: "Can I track tech hours per job?",
        answer:
          "Yes. Time entries track hours with approval workflow. Labor costs are logged against the estimate for budget vs. actuals.",
      },
      {
        question: "What about warranty callbacks?",
        answer:
          "Track warranty work as services linked to the original project. Log costs separately so you can see true job profitability.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, PMs, techs, or office staff. Role-based access controls who sees what.",
      },
    ],
  },
  cta: {
    title: "Run your HVAC business from one platform.",
    description:
      "Equipment costs, maintenance agreements, crew tracking, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded HVAC costs",
      "Cancel anytime",
    ],
  },
};
